// 003 — Hospitalización / Anamnesis-Examen Físico (Fase 7, Tanda 17). Hoja 1 idx 14 (A-F) y hoja 2 idx 15 (G-L), footer
// `SNS-MSP/HCU-form.003/2021`. Decisiones comunes con 002 en `bloquesAnamnesisDatos.ts`. Propio de 003: 6 motivos numerados,
// antecedentes personales de 11 categorías (con alergias) más 12. gineco-obstétricos y andrológicos (18 campos), constantes
// en una sola toma (perímetro cefálico, Score Mamá, Otros), Análisis (I), plan con rehabilitación y educacionales. La silueta
// corporal (4 figuras) NO se transcribe: equivalencia funcional, no facsímil (como 058/115); las regiones afectadas se
// describen en el examen físico. Las alergias marcadas (1 y 2) con su descripción siguen alimentando `AllergyIntolerance`.
import type { DiagnosticoConMarca } from './MarcaDiagnostico';
import { imcTexto } from './calculosClinicosDatos';
import { valoresVacios, type CampoSimple, type Valores } from './camposSimplesDatos';
import {
  CAMPOS_ENFERMEDAD_ANAMNESIS,
  CAMPOS_EXAMEN_REGIONAL,
  CAMPOS_EXAMEN_SISTEMICO,
  CAMPOS_FAMILIARES_ANAMNESIS,
  CAMPOS_REVISION_ANAMNESIS,
  casillasConDescribe,
  diagnosticosVacios,
  hallazgosExamenFisico,
  paresBloque,
  paresDiagnosticos,
} from './bloquesAnamnesisDatos';
import type { TomaSignos } from './signosVitalesToma';
import { resumenSilueta, type DatosSiluetaPdf } from '@logiacare/hce-pdf';
import type { Par } from './index';

const numero = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'numero' });
/** El papel pide la fecha, o "hace cuánto" (años/meses/días) si no la recuerda: por eso texto y no un campo de fecha. */
const fecha = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta });
const texto = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta });

export const NUM_MOTIVOS_003 = 6;
export const CAMPOS_MOTIVOS_003: readonly CampoSimple[] = Array.from({ length: NUM_MOTIVOS_003 }, (_, i) => ({ clave: `m${i + 1}`, etiqueta: `Motivo ${i + 1}`, tipo: 'area' as const }));

export const CAMPOS_PERSONALES_003 = casillasConDescribe(
  'ap',
  ['Alergia a medicamentos', 'Otras alergias', 'Vacunas', 'Patologías clínicas', 'Medicación habitual', 'Quirúrgicos', 'Hábitos', 'Condición socio económica', 'Discapacidad', 'Religión', 'Tipificación sanguínea'],
  'Describa (número y descripción)',
);

/** 12. Gineco-obstétricos y andrológicos — 18 campos; si no recuerda la fecha, anotar hace cuánto (años/meses/días). */
export const CAMPOS_GINECO_003: readonly CampoSimple[] = [
  numero('menarquia', 'Edad menarquia'),
  numero('menopausia', 'Edad menopausia'),
  texto('ciclos', 'Ciclos'),
  numero('inicioVidaSexual', 'Edad inicio vida sexual'),
  numero('gestas', 'Número gestas'),
  numero('partos', 'Número partos'),
  numero('abortos', 'Número abortos'),
  numero('cesareas', 'Número cesáreas'),
  numero('hijosVivos', 'Número hijos vivos'),
  fecha('fum', 'Fecha última menstruación'),
  fecha('ultimoParto', 'Fecha último parto'),
  fecha('citologia', 'Fecha última citología cervical'),
  fecha('colposcopia', 'Fecha última colposcopía'),
  fecha('mamografia', 'Fecha última mamografía'),
  texto('planificacion', 'Método de planificación familiar'),
  texto('terapiaHormonal', 'Terapia hormonal'),
  fecha('antigenoProstatico', 'Fecha último antígeno prostático'),
  fecha('ecoProstatico', 'Fecha último eco prostático'),
];

export const CAMPOS_CONSTANTES_003: readonly CampoSimple[] = [
  numero('temperatura', 'Temperatura (°C)'),
  texto('presion', 'Presión arterial (mmHg)'),
  numero('pulso', 'Pulso/min'),
  numero('fr', 'Frecuencia respiratoria/min'),
  numero('peso', 'Peso (Kg)'),
  numero('talla', 'Talla (cm)'),
  // IMC calculado desde peso y talla (solo lectura); el papel lo deriva de esas dos casillas.
  { ...numero('imc', 'IMC (Kg/m²)'), calculado: (v) => imcTexto(v.peso, v.talla) },
  numero('perimetroCefalico', 'Perímetro cefálico (cm)'),
  numero('spo2', 'Pulsioximetría (%)'),
  texto('scoreMama', 'Score Mamá'),
  texto('otros', 'Otros'),
];

export const CAMPOS_ANALISIS_003: readonly CampoSimple[] = [{ clave: 'analisis', etiqueta: 'Análisis (descripción de lo encontrado en el paciente)', tipo: 'area' }];
export const CAMPOS_PLAN_003: readonly CampoSimple[] = [{ clave: 'plan', etiqueta: 'Plan de tratamiento (diagnóstico, terapéutico, educacional y de rehabilitación)', tipo: 'area' }];

export interface Estado003 {
  readonly motivos: Valores;
  readonly personales: Valores;
  readonly gineco: Valores;
  readonly familiares: Valores;
  readonly enfermedad: Valores;
  readonly revision: Valores;
  readonly constantes: Valores;
  readonly regional: Valores;
  readonly sistemico: Valores;
  readonly analisis: Valores;
  /** Claves `<vista>:<región>` marcadas en la silueta corporal. */
  readonly silueta: readonly string[];
  readonly diagnosticos: readonly DiagnosticoConMarca[];
  readonly plan: Valores;
}

export function estadoInicial003(): Estado003 {
  return {
    motivos: valoresVacios(CAMPOS_MOTIVOS_003),
    personales: valoresVacios(CAMPOS_PERSONALES_003),
    gineco: valoresVacios(CAMPOS_GINECO_003),
    familiares: valoresVacios(CAMPOS_FAMILIARES_ANAMNESIS),
    enfermedad: valoresVacios(CAMPOS_ENFERMEDAD_ANAMNESIS),
    revision: valoresVacios(CAMPOS_REVISION_ANAMNESIS),
    constantes: valoresVacios(CAMPOS_CONSTANTES_003),
    regional: valoresVacios(CAMPOS_EXAMEN_REGIONAL),
    sistemico: valoresVacios(CAMPOS_EXAMEN_SISTEMICO),
    analisis: valoresVacios(CAMPOS_ANALISIS_003),
    silueta: [],
    diagnosticos: diagnosticosVacios(),
    plan: valoresVacios(CAMPOS_PLAN_003),
  };
}

/** Los motivos con texto, unidos — `Encounter.reasonCode`. */
export const motivoDe003 = (e: Estado003): string => CAMPOS_MOTIVOS_003.map((c) => (e.motivos[c.clave] ?? '').trim()).filter(Boolean).join('; ');
export const hallazgosDe003 = (e: Estado003): string => [hallazgosExamenFisico(e.regional, e.sistemico), e.silueta.length ? `Silueta: ${resumenSilueta(e.silueta)}` : ''].filter(Boolean).join('\n');

/**
 * Alergias: del "Describa" de antecedentes personales solo los renglones que empiezan con el número 1 (alergia a
 * medicamentos) o 2 (otras alergias) — el papel pide anotar el número de la categoría y su descripción — y solo si esa
 * casilla está marcada. Nada más del "Describa" (vacunas, quirúrgicos...) llega a `AllergyIntolerance`.
 */
export function alergiasDe003(e: Estado003): string[] {
  const marcadas = new Set([e.personales.ap1 === 'true' ? '1' : '', e.personales.ap2 === 'true' ? '2' : ''].filter(Boolean));
  if (marcadas.size === 0) return [];
  return (e.personales.apDescriba ?? '')
    .split(/[\n;]+/)
    .map((linea) => linea.trim())
    .flatMap((linea) => {
      const m = /^(\d{1,2})\s*[.\-:)]?\s*(.+)$/.exec(linea);
      return m && marcadas.has(m[1]!) && m[2]!.trim() ? [m[2]!.trim()] : [];
    });
}

/** La única toma de constantes de 003. */
export function tomasDe003(e: Estado003): TomaSignos[] {
  const c = e.constantes;
  return [{ temperatura: c.temperatura, presion: c.presion, fc: c.pulso, fr: c.fr, peso: c.peso, talla: c.talla, spo2: c.spo2 }];
}

/** Datos de la silueta para el PDF firmado (`undefined` si no se marcó nada). */
export const siluetaDe003 = (e: Estado003): DatosSiluetaPdf | undefined => (e.silueta.length > 0 ? { regiones: e.silueta } : undefined);

export function paresDe003(e: Estado003): Par[] {
  return [
    ...paresBloque(CAMPOS_MOTIVOS_003, e.motivos, ''),
    ...paresBloque(CAMPOS_PERSONALES_003, e.personales, 'Antecedentes patológicos personales — '),
    ...paresBloque(CAMPOS_GINECO_003, e.gineco, 'Gineco-obstétricos y andrológicos — '),
    ...paresBloque(CAMPOS_FAMILIARES_ANAMNESIS, e.familiares, 'Antecedentes patológicos familiares — '),
    ...paresBloque(CAMPOS_ENFERMEDAD_ANAMNESIS, e.enfermedad, ''),
    ...paresBloque(CAMPOS_REVISION_ANAMNESIS, e.revision, 'Revisión de órganos y sistemas — '),
    ...paresBloque(CAMPOS_CONSTANTES_003, e.constantes, 'Constantes vitales — '),
    ...paresBloque(CAMPOS_EXAMEN_REGIONAL, e.regional, 'Examen físico regional — '),
    ...paresBloque(CAMPOS_EXAMEN_SISTEMICO, e.sistemico, 'Examen físico sistémico — '),
    { pregunta: 'Silueta corporal — regiones marcadas', respuesta: resumenSilueta(e.silueta) },
    ...paresBloque(CAMPOS_ANALISIS_003, e.analisis, ''),
    ...paresDiagnosticos(e.diagnosticos),
    ...paresBloque(CAMPOS_PLAN_003, e.plan, ''),
  ].filter((p) => p.respuesta.trim() !== '');
}
